import { useCallback, useContext, useEffect, useState } from "react"
import { DContext } from "../../../context/Datacontext"
import Loading from "../../Loading"
import { fetchCart } from "../../../services/students/carts/fetchCart"
import { removeFromCart } from "../../../services/students/carts/removeFromCart"
import { createOrder } from "../../../services/students/courses/createOrder"
import {
  FiShoppingCart,
  FiTrash2,
  FiCheckCircle,
  FiClock,
  FiShield
} from "react-icons/fi"
import { BiRupee } from "react-icons/bi"

function Cart() {
  const [cart, setCart] = useState(null)
  const [removingId, setRemovingId] = useState(null)
  const [creatingId, setCreatingId] = useState(null)
  const [message, setMessage] = useState(null)
  const { BeURL } = useContext(DContext)

  const loadCart = useCallback(async () => {
    const data = await fetchCart({ BeURL });
    setCart(data);
  }, [BeURL]);

  useEffect(() => {
    if (BeURL) loadCart();
  }, [BeURL, loadCart]);

  const formatPrice = (price) => {
    const valid = typeof price === 'number' && price >= 0 ? price : 0
    return Math.round(valid).toLocaleString('en-IN')
  }

  const getFallbackThumbnail = (title) => {
    const initials = (title || 'Course')
      .split(' ')
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
    return initials.substring(0, 2)
  }

  const handleRemove = async (courseId) => {
    if (!courseId) return
    setRemovingId(courseId)
    const result = await removeFromCart({ BeURL, courseId })

    if (result?.success) {
      setCart(result.cart)
      setMessage({ type: 'success', text: 'Course removed from cart.' })
    } else if (result?.message) {
      setMessage({ type: 'error', text: result.message })
    }

    setRemovingId(null)
    setTimeout(() => setMessage(null), 2500)
  }

  const handleCreateOrder = async (courseId) => {
    if (!courseId) return
    setCreatingId(courseId)
    const result = await createOrder({ BeURL, courseId })

    if (result?.success) {
      await loadCart()
      setMessage({ type: 'success', text: 'Order created. Awaiting admin approval.' })
    } else if (result?.message) {
      setMessage({ type: 'error', text: result.message })
    }

    setCreatingId(null)
    setTimeout(() => setMessage(null), 2500)
  }

  if (!BeURL || !cart) return <Loading />

  const courses = Array.isArray(cart?.courses) ? cart.courses : []
  const pricing = cart?.pricing || { subtotal: 0, discount: 0, totalAmount: 0 }

  return (
    <div className="min-h-screen  p-4 sm:p-6 font-['Space_Grotesk']">
      <div className="max-w-7xl mx-auto">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-6 sm:p-8 mb-6 shadow-2xl">
          <div className="absolute -right-24 -top-24 w-52 h-52 rounded-full bg-emerald-400/20 blur-3xl" />
          <div className="absolute -left-10 -bottom-16 w-40 h-40 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 text-2xl sm:text-3xl font-semibold">
                <FiShoppingCart className="text-emerald-300" />
                Your Learning Cart
              </div>
              <p className="text-sm text-slate-300 mt-2">
                Review your picks and create orders when you are ready.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-sm">
              <FiShield />
              No taxes. No coupons. Manual approval.
            </div>
          </div>
        </div>

        {message && (
          <div className={`mb-6 rounded-xl border px-4 py-3 text-sm animate-in fade-in ${message.type === 'success'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-rose-50 border-rose-200 text-rose-800'
            }`}>
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_0.6fr] gap-6">
          <div className="space-y-4">
            {courses.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center shadow-sm">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <FiShoppingCart className="text-slate-500" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-slate-800">Your cart is empty</h3>
                <p className="text-sm text-slate-500 mt-2">Explore courses and add the ones you love.</p>
              </div>
            ) : (
              courses.map((course, index) => (
                <div
                  key={course.id}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden animate-slide-in-bottom"
                  style={{ animationDelay: `${index * 60}ms` }}
                >
                  <div className="flex flex-col sm:flex-row">
                    <div className="w-full sm:w-40 h-32 sm:h-auto bg-gradient-to-br from-emerald-100 via-cyan-50 to-transparent flex items-center justify-center">
                      {course.thumbnail ? (
                        <img src={`${BeURL}${course.thumbnail}`} alt={course.title} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl font-bold text-emerald-600">
                          {getFallbackThumbnail(course.title)}
                        </span>
                      )}
                    </div>
                    {/* <div className="flex-1 p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div>
                          <h4 className="text-lg font-semibold text-slate-900">{course.title || 'Untitled Course'}</h4>
                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
                            <FiClock size={14} />
                            Admin approval after buy course
                          </div>
                        </div>
                        <div className="text-right">
                          {typeof course.discountPrice === 'number' && course.discountPrice < course.price ? (
                            <div className="space-y-1">
                              <div className="text-sm text-slate-400 line-through flex items-center justify-end">
                                <BiRupee />
                                {formatPrice(course.price)}
                              </div>
                              <div className="text-xl font-semibold text-emerald-600 flex items-center justify-end">
                                <BiRupee />
                                {formatPrice(course.discountPrice)}
                              </div>
                            </div>
                          ) : (
                            <div className="text-xl font-semibold text-emerald-600 flex items-center justify-end">
                              <BiRupee />
                              {formatPrice(course.price)}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-3">
                        <button
                          onClick={() => handleCreateOrder(course.id)}
                          disabled={creatingId === course.id}
                          className={`
  flex items-center gap-2
  px-5 py-2.5
  rounded-xl
  text-sm font-semibold text-white
  bg-primary-600
  shadow-md hover:shadow-xl
  transition-all duration-300
  hover:scale-[1.02]
  ${creatingId === course.id ? "opacity-70 cursor-wait" : ""}
  `}
                        >
                          {creatingId === course.id ? (
                            <>
                              <span className="animate-pulse">Creating...</span>
                            </>
                          ) : (
                            <>
                              <FiShoppingCart size={16} />
                              Buy Now
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleRemove(course.id)}
                          disabled={removingId === course.id}
                          className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2"
                        >
                          <FiTrash2 size={14} />
                          {removingId === course.id ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    </div> */}
                    <div className="flex-1 p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">

                        {/* Course Info */}
                        <div>
                          <h4 className="text-lg font-semibold text-slate-900">
                            {course.title || "Untitled Course"}
                          </h4>

                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
                            <FiClock size={14} />
                            Admin approval after buying the course
                          </div>
                        </div>

                        {/* Price */}
                        <div className="text-right">
                          {typeof course.discountPrice === "number" &&
                            course.discountPrice < course.price ? (
                            <div className="space-y-1">

                              {/* Original Price */}
                              <div className="text-sm text-slate-400 line-through flex items-center justify-end">
                                <BiRupee />
                                {formatPrice(course.price)}
                              </div>

                              {/* Discount Price */}
                              <div className="text-xl font-bold text-emerald-600 flex items-center justify-end">
                                <BiRupee />
                                {formatPrice(course.discountPrice)}
                              </div>

                              {/* Discount Badge */}
                              <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-md">
                                Save ₹{course.price - course.discountPrice}
                              </span>

                            </div>
                          ) : (
                            <div className="text-xl font-bold text-emerald-600 flex items-center justify-end">
                              <BiRupee />
                              {formatPrice(course.price)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="mt-5 flex flex-wrap items-center gap-3">

                        {/* Buy Button */}
                        <button
                          onClick={() => handleCreateOrder(course.id)}
                          disabled={creatingId === course.id}
                          className={`
        flex items-center gap-2
        px-5 py-2.5
        rounded-xl
        text-sm font-semibold text-white
        bg-primary-600
        hover:bg-primary-700
        shadow-md hover:shadow-lg
        transition-all duration-200
        ${creatingId === course.id ? "opacity-70 cursor-wait" : ""}
      `}
                        >
                          {creatingId === course.id ? (
                            <>
                              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
                              Creating...
                            </>
                          ) : (
                            <>
                              <FiShoppingCart size={16} />
                              Buy Now
                            </>
                          )}
                        </button>

                        {/* Remove Button */}
                        <button
                          onClick={() => handleRemove(course.id)}
                          disabled={removingId === course.id}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
                        >
                          <FiTrash2 size={14} />
                          {removingId === course.id ? "Removing..." : "Remove"}
                        </button>

                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Summary</h3>
                <FiCheckCircle className="text-emerald-500" />
              </div>
              <div className="space-y-3 text-sm text-slate-600">
                <div className="flex items-center justify-between">
                  <span>Subtotal</span>
                  <span className="font-medium text-slate-900 flex items-center">
                    <BiRupee /> {formatPrice(pricing.subtotal)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Discount</span>
                  <span className="font-medium text-emerald-600 flex items-center">
                    - <BiRupee /> {formatPrice(pricing.discount)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t pt-3 text-base">
                  <span className="font-semibold text-slate-900">Total</span>
                  <span className="font-semibold text-slate-900 flex items-center">
                    <BiRupee /> {formatPrice(pricing.totalAmount)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white shadow-lg">
              <h4 className="text-lg font-semibold mb-2">What happens next?</h4>
              <p className="text-sm text-slate-300 leading-relaxed">
                Once you buy the course, our admin team will review the order and grant you access.
              </p>
              <div className="mt-4 flex items-center gap-2 text-sm text-emerald-300">
                <FiClock />
                Access is typically granted within 1–2 business days
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Cart